import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import {
  getCouponById as fetchCouponByIdThunk,
  getTrendingCoupons,
} from "../store/actions/couponActions";
import { recordCouponViewOnce } from "../store/actions/couponViewActions";
import {
  upsertCouponVote,
  removeCouponVote,
} from "../store/actions/couponVoteActions";
import {
  fetchCouponComments,
  createCouponComment,
  deleteCouponComment,
} from "../store/actions/couponCommentActions";
import { addCouponLike, removeCouponLike } from "../store/actions/couponLikeActions";
import { cPath } from "../constants";
import { storeSlugForCoupon } from "../utils/storeSlugForCoupon";
import { Container } from "../components/layout/Container";
import CouponBreadcrumb from "../components/coupon-detail/CouponBreadcrumb";
import CouponMainDealCard from "../components/coupon-detail/CouponMainDealCard";
import CouponTabsSection from "../components/coupon-detail/CouponTabsSection";
import PopularDealsSidebar from "../components/coupon-detail/PopularDealsSidebar";
import CrawlerSeoContent from "../components/seo/CrawlerSeoContent";
import RelatedStoresStrip from "../components/seo/RelatedStoresStrip";
import useSEO from "../hooks/useSEO";
import seoConfig from "../config/seoConfig";
import {
  generateOfferSchema,
  generateProductSchema,
  generateBreadcrumbSchema,
} from "../utils/schemaGenerators";
import { getLocalizedCoupon } from "../utils/getLocalizedStore";

const NotFound = styled.div`
  text-align: center;
  padding: 5rem 1rem;
`;

const Main = styled.div`
  padding: 2rem 0;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(1.25rem, 2vw, 2.5rem);
  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const ColMain = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  width: 100%;
`;

const ColSide = styled.div`
  width: 100%;
  @media (min-width: 1024px) {
    width: clamp(14rem, 17vw, 18rem);
    flex: 0 0 clamp(14rem, 17vw, 18rem);
  }
`;

const LoadNote = styled.p`
  text-align: center;
  padding: 3rem 1rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

export default function CouponDetailPage() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { id } = useParams();
  const couponId = id || "";

  const couponApi = useSelector((s) => s.coupon.coupon);
  const trendingFromApi = useSelector((s) => s.coupon.trendingCoupons) || [];
  const couponError = useSelector((s) => s.coupon.error);
  const { isAuthenticated, user } = useSelector((s) => s.auth);
  const voteState = useSelector((s) => s.couponVote);
  const likeState = useSelector((s) => s.couponLike);
  const commentState = useSelector((s) => s.couponComment);

  const [activeTab, setActiveTab] = useState("details");
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setPageLoading(true);
    (async () => {
      await dispatch(fetchCouponByIdThunk(couponId));
      if (cancelled) return;
      await dispatch(getTrendingCoupons(10));
      if (!cancelled) setPageLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [couponId, dispatch]);

  const couponRaw =
    couponApi && String(couponApi.id) === String(couponId) ? couponApi : null;

  const coupon = useMemo(
    () => getLocalizedCoupon(couponRaw, i18n.language),
    [couponRaw, i18n.language]
  );

  useEffect(() => {
    if (pageLoading || !coupon) return;
    dispatch(recordCouponViewOnce(couponId));
  }, [couponId, pageLoading, coupon, dispatch]);

  useEffect(() => {
    if (!couponId || activeTab !== "comments" || !coupon) return;
    dispatch(fetchCouponComments(couponId));
  }, [couponId, activeTab, coupon, dispatch]);

  const voteBusy = voteState.upsertLoading || voteState.removeLoading;
  const likeBusy =
    likeState.toggleLoading && likeState.toggleCouponId === coupon?.id;

  const handleVoteUp = useCallback(() => {
    if (!coupon?.id) return;
    if (!isAuthenticated) {
      window.alert("Sign in to vote on this deal.");
      return;
    }
    if (coupon.myVote === 1) {
      dispatch(removeCouponVote(coupon.id));
    } else {
      dispatch(upsertCouponVote(coupon.id, 1));
    }
  }, [coupon, isAuthenticated, dispatch]);

  const handleVoteDown = useCallback(() => {
    if (!coupon?.id) return;
    if (!isAuthenticated) {
      window.alert("Sign in to vote on this deal.");
      return;
    }
    if (coupon.myVote === -1) {
      dispatch(removeCouponVote(coupon.id));
    } else {
      dispatch(upsertCouponVote(coupon.id, -1));
    }
  }, [coupon, isAuthenticated, dispatch]);

  const handleBookmark = useCallback(() => {
    if (!coupon?.id) return;
    if (!isAuthenticated) {
      window.alert("Sign in to save this coupon.");
      return;
    }
    if (coupon.isLiked) {
      dispatch(removeCouponLike(coupon.id));
    } else {
      dispatch(addCouponLike(coupon.id));
    }
  }, [coupon, isAuthenticated, dispatch]);

  const handlePostComment = useCallback(
    async (body) => {
      if (!coupon?.id || !body?.trim()) return false;
      if (!isAuthenticated) {
        window.alert("Sign in to post a comment.");
        return false;
      }
      return dispatch(createCouponComment(coupon.id, { body: body.trim() }));
    },
    [coupon, isAuthenticated, dispatch]
  );

  const handleDeleteComment = useCallback(
    (commentId) => {
      if (!coupon?.id) return;
      dispatch(deleteCouponComment(coupon.id, commentId));
    },
    [coupon, dispatch]
  );

  const { popularDeals, trendingDeals } = useMemo(() => {
    if (!trendingFromApi.length) {
      return { popularDeals: [], trendingDeals: [] };
    }
    const popular = [...trendingFromApi].sort(
      (a, b) => (b.upvotes || 0) - (a.upvotes || 0)
    );
    const trending = [...trendingFromApi].sort(
      (a, b) => (b.views || 0) - (a.views || 0)
    );
    return {
      popularDeals: popular.slice(0, 5),
      trendingDeals: trending.slice(0, 5),
    };
  }, [trendingFromApi]);

  const slug = coupon ? storeSlugForCoupon(coupon) : "";
  const allStores = useSelector((s) => s.store.stores) || [];
  const merchantStore = useMemo(
    () => allStores.find((s) => s.slug === slug),
    [allStores, slug]
  );
  const merchantUrl = merchantStore?.websiteUrl || "";
  const relatedStores = useMemo(() => {
    if (!coupon?.categorySlug) return allStores.slice(0, 8);
    return allStores.filter((s) => s.categorySlug === coupon.categorySlug);
  }, [allStores, coupon]);

  const couponSeo = coupon?.seo;
  const seo = useSEO({
    title: coupon
      ? couponSeo?.title || coupon.title
      : undefined,
    description: coupon
      ? couponSeo?.description ||
        (coupon.description && coupon.description.replace(/<[^>]+>/g, "").slice(0, 160)) ||
        `Get ${coupon.discountValue || "savings"} at ${coupon.storeName || "this store"}.`
      : undefined,
    keywords: couponSeo?.keywords
      ? String(couponSeo.keywords).split(/[,;]+/).map((k) => k.trim()).filter(Boolean)
      : undefined,
    canonical: couponSeo?.canonicalUrl || undefined,
    image: couponSeo?.ogImage || coupon?.productImage || coupon?.storeLogo,
    noindex: couponSeo?.robots?.includes("noindex"),
    nofollow: couponSeo?.robots?.includes("nofollow"),
    schema: coupon
      ? [
          generateOfferSchema(coupon, { baseUrl: seoConfig.siteUrl }),
          generateProductSchema(coupon),
          generateBreadcrumbSchema([
            { name: "Home", url: seoConfig.siteUrl },
            { name: "Coupons", url: `${seoConfig.siteUrl}/coupons` },
            {
              name: coupon.title,
              url: `${seoConfig.siteUrl}/coupon/${coupon.id}`,
            },
          ]),
        ].filter(Boolean)
      : undefined,
  });

  if (pageLoading) {
    return (
      <>
        {seo}
        <LoadNote>Loading coupon…</LoadNote>
      </>
    );
  }

  if (!coupon) {
    return (
      <>
        {seo}
        <NotFound>
        <h1 style={{ marginBottom: "1rem" }}>Coupon not found</h1>
        {couponError ? (
          <p style={{ color: "hsl(220 10% 46%)" }}>{couponError}</p>
        ) : null}
        <Link to={cPath("")}>← Back to home</Link>
        </NotFound>
      </>
    );
  }

  const copyCode = () => {
    if (!coupon?.code) return;
    navigator.clipboard.writeText(coupon.code);
    import("../utils/analytics").then(({ trackCouponCopy }) => {
      trackCouponCopy({
        couponId: coupon.id,
        storeSlug: slug,
        hasCode: true,
      });
    });
  };

  const engagementError =
    voteState.error || likeState.toggleError || commentState.error || null;

  return (
    <>
      {seo}
      <CouponBreadcrumb coupon={coupon} storeSlug={slug} />
      {coupon && (
        <CrawlerSeoContent
          title={coupon.title}
          intro={coupon.description}
        />
      )}
      <Main>
        <Container>
          <Grid>
            <ColMain>
              <CouponMainDealCard
                coupon={coupon}
                live
                isAuthenticated={isAuthenticated}
                voteBusy={Boolean(voteBusy)}
                likeBusy={Boolean(likeBusy)}
                engagementError={engagementError}
                onVoteUp={handleVoteUp}
                onVoteDown={handleVoteDown}
                onBookmarkToggle={handleBookmark}
                onCopyCode={copyCode}
                storeSlug={slug}
                merchantUrl={merchantUrl}
              />
              <RelatedStoresStrip
                title="More stores in this category"
                stores={relatedStores}
                excludeSlug={slug}
              />
              <CouponTabsSection
                coupon={coupon}
                activeTab={activeTab}
                onTab={setActiveTab}
                isAuthenticated={isAuthenticated}
                currentUserId={user?.id}
                comments={commentState.list}
                commentsLoading={commentState.loading}
                commentsSubmitting={commentState.submitting}
                onPostComment={handlePostComment}
                onDeleteComment={handleDeleteComment}
                mutatingCommentId={commentState.mutatingId}
              />
            </ColMain>
            <ColSide>
              <PopularDealsSidebar
                popularDeals={popularDeals}
                trendingDeals={trendingDeals}
              />
            </ColSide>
          </Grid>
        </Container>
      </Main>
    </>
  );
}
